import type { ReactNode } from "react";

import { usePermission } from "@/shared/hooks/usePermission";

import { AcessoNegado } from "./AcessoNegado";

interface PermissionGuardProps {
  /** Nome do módulo conforme registrado no backend (ex.: "Configurações"). */
  modulo?: string;
  /**
   * Sigla da ação requerida (ex.: "CONS", "VER").
   * Se omitido, basta ter qualquer ação no módulo.
   */
  acao?: string;
  /**
   * Lista alternativa de permissões: o acesso é liberado se o usuário tiver
   * **qualquer uma** delas. Útil para telas com múltiplos pontos de entrada
   * (ex.: Movimentações = VER em Movimentações OU CRIAR em Ajuste/Inventário).
   * Quando informada, `modulo`/`acao` são ignorados.
   */
  permissoes?: ReadonlyArray<{ modulo: string; acao?: string }>;
  children: ReactNode;
}

/**
 * Bloqueia o acesso à tela quando o usuário não tem a permissão necessária.
 * Usa as permissões cacheadas no Redux (atualizadas pelo /me).
 */
export function PermissionGuard({ modulo, acao, permissoes, children }: PermissionGuardProps) {
  const { hasPermission, hasAnyPermission, hasAnyOf } = usePermission();

  const allowed = permissoes
    ? hasAnyOf(permissoes)
    : modulo
      ? acao
        ? hasPermission(modulo, acao)
        : hasAnyPermission(modulo)
      : true;

  if (!allowed) return <AcessoNegado />;
  return <>{children}</>;
}
